// Details panel for a support-company card (opened by the info icon / "About this referral").
// Bottom sheet on mobile, right-hand drawer on desktop.

import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ThemeColours } from '@/clients/acme/config/colours';
import { resolveSupportCompanyLogo } from './supportCompanyLogo';

export interface SupportDetailsSection {
  heading: string;
  paragraphs?: readonly string[];
  checks?: readonly string[];
  numbered?: readonly string[];
}

export interface SupportDetailsContent {
  logo?: string;
  title: string;
  subtitle?: string;
  sections: readonly SupportDetailsSection[];
  footnoteHeading?: string;
  footnote?: string;
}

interface SupportDetailsPanelProps {
  open: boolean;
  onClose: () => void;
  content: SupportDetailsContent;
  testIdPrefix: string;
}

const SupportDetailsPanel: React.FC<SupportDetailsPanelProps> = ({
  open,
  onClose,
  content,
  testIdPrefix,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const logoSrc = resolveSupportCompanyLogo(content.logo);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      sx={{
        // Sits above the Additional Support dialog underneath
        zIndex: (theme.zIndex?.modal ?? 1300) + 2,
        '& .MuiDrawer-paper': isMobile
          ? {
              maxHeight: '92vh',
              borderRadius: '16px 16px 0 0',
              backgroundImage: 'none',
            }
          : {
              width: { md: 'min(480px, 40vw)', lg: 'min(520px, 36vw)' },
              borderRadius: '16px 0 0 16px',
              backgroundImage: 'none',
            },
      }}
    >
      <Box data-testid={`${testIdPrefix}-details-panel`} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'action.disabled', opacity: 0.6 }} />
          </Box>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
          {logoSrc && (
            <Box
              component="img"
              src={logoSrc}
              alt={`${content.title} logo`}
              data-testid={`${testIdPrefix}-details-logo`}
              sx={{
                width: { xs: 80, sm: 88 },
                aspectRatio: '88 / 48',
                objectFit: 'contain',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                p: 0.5,
                flexShrink: 0,
                bgcolor: 'white',
              }}
            />
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {content.title}
            </Typography>
            {content.subtitle && (
              <Typography variant="body2" sx={{ color: ThemeColours.subtitleVar2 }}>
                {content.subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="close"
            data-testid={`${testIdPrefix}-details-close`}
            onClick={onClose}
            sx={{ color: '#6b7280', alignSelf: 'flex-start' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {/* Body */}
        <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
          {content.sections.map((section) => (
            <Box key={section.heading} sx={{ mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {section.heading}
              </Typography>

              {section.paragraphs?.map((text) => (
                <Typography key={text} variant="body1" sx={{ color: ThemeColours.bodyVar1 }}>
                  {text}
                </Typography>
              ))}

              {section.checks?.map((text) => (
                <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 0.5 }}>
                  <CheckCircleIcon sx={{ color: 'green', fontSize: 14, mt: '2px' }} />
                  <Typography variant="body1" sx={{ color: ThemeColours.bodyVar1 }}>
                    {text}
                  </Typography>
                </Box>
              ))}

              {section.numbered?.map((text, index) => (
                <Typography key={text} variant="body1" sx={{ color: ThemeColours.bodyVar1, mb: 0.75 }}>
                  {index + 1}. {text}
                </Typography>
              ))}
            </Box>
          ))}

          {content.footnote && (
            <>
              <Divider sx={{ mb: 2 }} />
              {content.footnoteHeading && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                  {content.footnoteHeading}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {content.footnote}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SupportDetailsPanel;
